import { toast } from "react-toastify";
import { fetchUserIp, loadColorConfig, shouldShowWagerNotice } from "../constants/common";
import API from "../services";
import { setActiveWallet, setExchangeAuthData, setSportBookAuthData, setUserDetails, setUserThumbnail, setWallets, setColorConfig, setSiteConfig, setDynamicTextColors, setGroupConfigFatalMessage } from "./authReducer";
import { setIsCashoutEnabled, setTurboCashoutCommission } from "./exchangeSports/exchangeSportsReducer";
import { ColorConfig, UserDetails } from "../models/UserDetails";
import { themes } from "../components/WalletComponents/MyProfile/MyProfile";
import { DateRange } from "../App";
import TOKEN_API from "../services/token";
import { SiteConfig } from "../models/SiteConfig";
import type { GroupConfigApiResponse } from "../models/GroupConfigResponse";
import store from "./store";


export const getGameUrl = (provider: string) => {
  return async (dispatch: Function) => {
    try {
      const userIp = await fetchUserIp();
      const response = await API.post(`/webapp/get-login-url`, {
        userKey: sessionStorage.getItem("userKey"),
        tgProviderId: provider,
        clientIp: userIp,
      });

      if (response.data.status !== "RS_OK") {
        toast.error(response.data.errorDescription);
        return Promise.reject(response.data.errorDescription);
      }

      if (response.data?.sessionUrl !== "") {
        const isCashoutEnabled = response.data?.isCashoutEnabled ?? false;
        const cashoutCommission = response.data?.cashoutCommission ?? 0;
        await setSportsAuthData(dispatch, provider, response.data.sessionUrl, isCashoutEnabled, cashoutCommission);
      }
      return Promise.resolve(response.data.sessionUrl);
    } catch (e) {
      console.error("API Error:", e);
      toast.error("Failed to get game URL. Please try again.");
      return Promise.reject(e);
    }
  };
};

export const getUserBalance = () => async (dispatch: any) => {
  let response: any;
  try {
    response = await API.post(`/webapp/get-user-balance`, {
      userKey: sessionStorage.getItem("userKey"),
    });
  } catch (e) {
    return;
  }

  // if (response.data.status !== "RS_OK") {
  //   toast.error(response.data.errDescription);
  // }

  if (response && response.data.wallets) {
    dispatch(setWallets(response.data?.wallets));
    dispatch(setActiveWallet(response?.data?.activeWallet))
  }
};

export const getRefId = (start?: string | null) => async (dispatch: any) => {
  try {
    const userKey = sessionStorage.getItem("userKey");
    if (!userKey) {
      console.error("User ID not found in session storage");
      return null;
    }
    const sharelinkkey = start?.trim() || "";
    const response = await API.post(`/users/get-user-details`, {
      userKey: userKey,
      sharelinkkey,
    });

    if (!response || response.data.status !== "RS_OK") {
      console.error("Error retrieving user details or invalid status");
      return null;
    }

    const fetchedUserDetails: UserDetails = response.data?.userDetails;
    const fetchedColors: ColorConfig=response.data?.colorConfig;
    const redirectStartParam = response?.data?.shareLinkEncoded || null;
    const validThemes = [
      'blue', 'purple', 'green', 'yellow', 'orange', // Existing themes
      'CSK', 'DC', 'GT', 'KKR', 'LSG', 'MI', 'PK', 'RR', 'RCB', 'SRH' // IPL Teams
    ] as const;
    type ThemeType = typeof validThemes[number];
    if (fetchedColors) {
      dispatch(setColorConfig(fetchedColors));
    }

    const rawAllowWl = response.data?.allowWhitelabel;
    const allowWhitelabel: boolean | "" =
      rawAllowWl === null || rawAllowWl === undefined ? "" : Boolean(rawAllowWl);

    if (fetchedUserDetails) {
      dispatch(setUserDetails({ ...fetchedUserDetails, allowWhitelabel }));
      if (fetchedUserDetails?.UserKey !== userKey) {
        sessionStorage.setItem("userKey", fetchedUserDetails?.UserKey);
      }
      dispatch(setUserThumbnail(fetchedUserDetails?.Thumbnail));
      if (fetchedUserDetails?.disableDefaultTheme || fetchedUserDetails.Theme == 'default'){
        setReposneTheme(fetchedColors)
      }else if (validThemes.includes(fetchedUserDetails.Theme as ThemeType)) {
        setTheme(fetchedUserDetails.Theme as ThemeType);
      } else {
        setTheme('blue');
      }
      
      // Compute and store dynamic text colors after theme is set
      // Use setTimeout to ensure CSS variables are updated before reading them
      setTimeout(() => {
        const dynamicColors = getDynamicTextColors(fetchedUserDetails);
        dispatch(setDynamicTextColors(dynamicColors));
      }, 100);
    } else {
      dispatch(setUserDetails({ allowWhitelabel }));
      console.error("User details not found in response");
    }

    return redirectStartParam;
  } catch (error) {
    console.error("Error fetching user details:", error);
    return null;
  }
};

const setTheme = (themeName: keyof typeof themes) => {
  const selectedTheme = themes[themeName];

  if (selectedTheme) {
    loadColorConfig(selectedTheme)
  } else {
    console.error('Theme not found');
  }
};

const setReposneTheme = (colors: ColorConfig) => {
  const selectedTheme = {
    inputboxColor:colors.inputBackgroundBox,
    appbgColor: colors.background,
    headerColor: colors.primary,
    filterColor: colors.filterColor,
    tablebg: colors.tableBackground,
    tableHeader: colors.tableHeader,
    tableHeadCellBorder: colors.tableBorder,
    marketTableBg: colors.tableBackground,
    ionColorPrimary: colors.primaryTeamInfo,
    teamInfoBackground: colors.backgroundTeamInfo,
    borderColor:colors.primary,
    backgroundCard:colors.tableBackground,
    meitmsColor: `${colors.primary}50`,
  };
  loadColorConfig(selectedTheme)
};


const setSportsAuthData = async (dispatch: Function, provider: string, url: string, isCashoutEnabled: boolean, cashoutCommission: number) => {
  return new Promise((resolve) => {
    const parsedUrl = new URL(url);

    const authData = {
      token: parsedUrl.searchParams.get("token"),
      operatorId: parsedUrl.searchParams.get("operatorId"),
      partnerId: parsedUrl.searchParams.get("partnerId"),
      providerId: parsedUrl.searchParams.get("providerId"),
      currency: parsedUrl.searchParams.get("currency"),
      rate: parseFloat(parsedUrl.searchParams.get("rate") || "1"),
    };

    let userDetails = store.getState().userDetails.userDetails;

    // Set the extracted data
    if (provider === userDetails?.exchangeProvider) {
      dispatch(setIsCashoutEnabled(isCashoutEnabled));
      dispatch(setTurboCashoutCommission(cashoutCommission));
      dispatch(setExchangeAuthData(authData));
    } else {
      dispatch(setSportBookAuthData(authData));
    }

    resolve(true);
  });
};

export function setTextColor(hexColor: any) {
  var r = parseInt(hexColor.substring(1, 3), 16);
  var g = parseInt(hexColor.substring(3, 5), 16);
  var b = parseInt(hexColor.substring(5, 7), 16);
  var yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= 186 ? "dark-text" : "light-text";
}

// Helper function to check if predefined theme is active
export function isPredefinedTheme(theme: string | undefined): boolean {
  if (!theme) return false;
  const predefinedThemes = ['blue', 'green', 'yellow', 'orange', 'purple'];
  return predefinedThemes.includes(theme.toLowerCase());
}

// Helper function to convert HSL to RGB
function hslToRgb(hsl: string): { r: number; g: number; b: number } | null {
  // Match HSL format: hsl(275deg 45% 9% / 95%) or hsl(275, 45%, 9%) or hsla(275, 45%, 9%, 0.95)
  // Try comma-separated format first: hsl(275, 45%, 9%)
  let hslMatch = hsl.match(/(\d+(?:\.\d+)?)\s*(?:deg)?\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%/i);
  
  // If not found, try space-separated format: hsl(275deg 45% 9%)
  if (!hslMatch) {
    hslMatch = hsl.match(/(\d+(?:\.\d+)?)\s*(?:deg)?\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/i);
  }
  
  if (!hslMatch) return null;
  
  const h = parseFloat(hslMatch[1]) / 360;
  const s = parseFloat(hslMatch[2]) / 100;
  const l = parseFloat(hslMatch[3]) / 100;
  
  let r, g, b;
  
  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  
  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

// Helper function to convert RGB/RGBA to hex
function rgbToHex(rgb: string): string {
  const rgbMatch = rgb.match(/\d+/g);
  if (rgbMatch && rgbMatch.length >= 3) {
    const r = parseInt(rgbMatch[0]);
    const g = parseInt(rgbMatch[1]);
    const b = parseInt(rgbMatch[2]);
    return `#${[r, g, b].map(x => {
      const hex = x.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    }).join('')}`;
  }
  return '';
}

// Helper function to get text color class from CSS variable
// Always checks the actual background color to determine text color dynamically
// Returns "light-text" (white) if predefined theme is selected (blue, green, yellow, orange, purple)
export function getTextColorFromCSSVar(
  cssVarName: string,
  userTheme: string | undefined,
  disableDefaultTheme: boolean
): string {
  
  // Always check the actual CSS variable value first to determine text color dynamically
  const bgColor = getComputedStyle(document.documentElement).getPropertyValue(cssVarName).trim();
  
  if (!bgColor) {
    // Fallback: For predefined themes, use white text if no CSS variable found
    if (!disableDefaultTheme && isPredefinedTheme(userTheme)) {
      return "light-text";
    }
    return "";
  }
  
  // Handle HSL/HSLA colors (check first as they might contain 'rgb' in the string)
  if (bgColor.toLowerCase().startsWith('hsl')) {
    const rgb = hslToRgb(bgColor);
    if (rgb) {
      const hex = `#${[rgb.r, rgb.g, rgb.b].map(x => {
        const hex = x.toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      }).join('')}`;
      const result = setTextColor(hex);
      return result;
    } else {
    }
  }
  
  // Handle rgb/rgba colors
  if (bgColor.startsWith('rgb')) {
    const hex = rgbToHex(bgColor);
    if (hex) {
      const result = setTextColor(hex);
      return result;
    } else {
    }
  }
  
  // Handle hex colors
  if (bgColor.startsWith('#')) {
    const result = setTextColor(bgColor);
    return result;
  }
  
  // Fallback: For predefined themes, use white text if color format is unrecognized
  if (!disableDefaultTheme && isPredefinedTheme(userTheme)) {
    return "light-text";
  }
  return "";
}

// Helper function to get all common dynamic text color classes
// Returns an object with pageTextColorClass, inputBoxTextColorClass, buttonTextColorClass, and accordionHeaderTextColorClass
export function getDynamicTextColors(userDetails: any) {
  const theme = userDetails?.Theme;
  const disableDefaultTheme = userDetails?.disableDefaultTheme || false;

  const inputBoxTextColorClass = getTextColorFromCSSVar(
    '--inputbox-color',
    theme,
    disableDefaultTheme
  );

  const buttonTextColorClass = getTextColorFromCSSVar(
    '--border-color',
    theme,
    disableDefaultTheme
  );

  const accordionHeaderTextColorClass = getTextColorFromCSSVar(
    '--ion-background-color',
    theme,
    disableDefaultTheme
  );

  return {
    pageTextColorClass: getTextColorFromCSSVar(
      '--app-bg-color',
      theme,
      disableDefaultTheme
    ),
    inputBoxTextColorClass,
    buttonTextColorClass,
    accordionHeaderTextColorClass,
    tabTextColorClass: inputBoxTextColorClass,
    activeTabTextColorClass: buttonTextColorClass,
    tableHeaderTextColorClass: getTextColorFromCSSVar(
      '--table-sub-header',
      theme,
      disableDefaultTheme
    ),
    tableRowTextColorClass: getTextColorFromCSSVar(
      '--market-table-bg',
      theme,
      disableDefaultTheme
    ),
    tableTextColorClass: getTextColorFromCSSVar(
      '--table-bg',
      theme,
      disableDefaultTheme
    ),
    sbMarketNameTextColorClass: getTextColorFromCSSVar(
      '--sb-mkt-name-bg',
      theme,
      disableDefaultTheme
    ),
    tabButtonTextColorClass: getTextColorFromCSSVar(
      '--table-head-cell-border',
      theme,
      disableDefaultTheme
    ),
    accordionSummaryTextColorClass: getTextColorFromCSSVar(
      '--accoridan-summary-background',
      theme,
      disableDefaultTheme
    ),
    teamInfoTextColorClass: getTextColorFromCSSVar(
      '--team-info-background',
      theme,
      disableDefaultTheme
    ),
    ionButtonTextColorClass: getTextColorFromCSSVar(
      '--ion-button-color',
      theme,
      disableDefaultTheme
    ),
    backgroundCardTextColorClass: getTextColorFromCSSVar(
      '--background-card',
      theme,
      disableDefaultTheme
    ),
    headerTextColorClass: getTextColorFromCSSVar(
      '--header-color',
      theme,
      disableDefaultTheme
    ),
  };
}

export const setUserSportsView = (isClassic: boolean) => {
  try {
    let result = null;
    result = API.post(`/users/change-sports-view`, {
      isClassic: isClassic,
    });

  } catch (err) {
    console.log(err);
  }
};

const decodeBase64 = (base64: string) => {
  try {
    const decodedString = atob(base64); // Decode Base64
    return JSON.parse(decodedString); // Convert to JSON
  } catch (error) {
    console.error("Error decoding Base64:", error);
    return null;
  }
};

export const BannerNavigation = (redirectUrl: string, navigate: (path: string, state?: any) => void) => {

  const jsonData = decodeBase64(redirectUrl);

  if (jsonData && jsonData.page) {
    const { page, ...rest } = jsonData;

    switch (page.toLowerCase()) {
      case "event":
        if (rest.id && rest.name && rest.lobby) {
          navigate(`/events/${btoa(`${rest.id}#${rest.name}#${rest.lobby}`)}`);
        }
        break;

      case "market":
        if (rest.id && rest.lobby) {
          const marketPath = rest.lobby.toUpperCase() === "SPORTSBOOK"
            ? `/sb-markets/${btoa(`${rest.id}`)}`
            : `/markets/${btoa(`${rest.id}`)}`;
          sessionStorage.setItem("previousPath", "/sports");
          navigate(marketPath);
        }
        break;

      case "customersupport":
        navigate(`/customersupport`)
        break;
      case "bonus":
        navigate("/wallet", { state: {bpCategory: rest?.bonus_category, bpType: rest?.bonus_type, tId: rest?.transaction_id, amount: rest?.amount, curType: rest?.currency?.type, curCategory: "", dateRange: rest?.date_range as DateRange | undefined, tab: "bonus", policyId: rest?.bonus_policy_id } });
        break;
      case "ticket-details":
        navigate("/ticket-details", { state: { ticketId: rest?.ticketId } });
        break;
      case "playgame":
        {
          const playPath = `/play/${btoa(`${rest?.id}:${rest?.id}:${rest?.tgProviderId}:${rest?.provider}:${rest?.subProvider}:${rest?.type}:${rest?.category}:${rest?.tableId ? rest?.tableId : ""}:${false}:${false}:${rest?.fantastyMetaData}`)}`;
          if (shouldShowWagerNotice(rest?.is_wager)) {
            window.dispatchEvent(new CustomEvent("pg:wagerNotice", { detail: { path: playPath } }));
          } else {
            navigate(playPath);
          }
        }
        break;
      case "home":
        navigate(`/home`)
        break;
      case "casino":
        navigate(`/casino`)
        break;
      case "sports":
        navigate(`/sports`)
        break;
      case "site":
        navigate(`/promotions`, { state: { Pagetab: "site" } })
        break;
      case "raffles": {
        const restRec = rest as Record<string, unknown>;
        const bonusCategory = String(restRec?.bonus_category ?? "").trim().toUpperCase();
        let raffleId: string | undefined;
        if (bonusCategory === "RAFFLE") {
          const pid = restRec?.bonus_policy_id;
          if (pid != null && String(pid).trim() !== "") {
            raffleId = String(pid).trim();
          }
        }
        if (!raffleId) {
          const rid = rest?.raffleId ?? restRec?.raffle_id;
          if (rid != null && String(rid).trim() !== "") {
            raffleId = String(rid).trim();
          }
        }
        navigate(`/promotions`, {
          state: {
            Pagetab: "raffles",
            ...(raffleId ? { raffleId } : {}),
          },
        });
        break;
      }
      case "wallet": {
        const wantsSpin = ["tab", "Pagetab", "walletTab", "activeTab"].some(
          (key) => String((rest as Record<string, unknown>)?.[key] ?? "").toLowerCase() === "spin"
        );
        if (wantsSpin) {
          navigate(`/wallet`, { state: { tab: "spin" } })
        } else {
          navigate(`/wallet`)
        }
        break;
      }
      case "promotions":
        navigate(`/promotions`, { state: { Pagetab: "promotions" } })
        break;
      case "spin":
        navigate(`/wallet`, { state: { tab: "spin" } })
        break;
      case "providergames":
        const prov = {
          provider: rest?.provider,
          subProvider: rest?.subProvider,
          tgProviderId: rest?.tgProviderId,
          thumbnail: rest?.thumbnail,
        }
        sessionStorage.setItem("previousPath", "/casino");
        navigate("/providergames", { state: { prov } });
        break;
      case "profilepage":
        sessionStorage.setItem("previousPath", "/profile");
        if (rest?.profileOption) {
          switch (rest?.profileOption) {
            case "myprofile":
              navigate(`/myprofile`)
              break;
            case "transactions":
              navigate(`/transactions`)
              break;
            case "accountstatement":
              navigate(`/account-statement`)
              break;
            case "faq":
              navigate(`/faq`)
              break;
            case "betlist":
              navigate(`/betlist`)
              break;
            case "mybrands":
              navigate(`/whitelabel`)
              break;
          }
        }
        break;
      default:
        console.warn("Unknown page type or missing required fields:", jsonData);
    }
  }
};

export const siteConfig = () => async (dispatch: any): Promise<{ blocked: boolean }> => {
  // Used only for channel message selection (`telegramMessage` vs `webMessage`)
  const isTelegram = !!(window as any)?.Telegram?.WebApp?.initDataUnsafe?.user;

  try {
    const response = await TOKEN_API.post(`/api/v1/auth/group-config`, {});
    const data = response?.data as GroupConfigApiResponse | undefined;

    if (!data || typeof data.status !== "string") {
      toast.error("Unable to load site configuration.");
      dispatch(setGroupConfigFatalMessage(null));
      return { blocked: false };
    }

    // `status !== "RS_OK"` is the fatal/non-fatal path described by you.
    if (data.status !== "RS_OK") {
      const rawMessage = isTelegram ? data.telegramMessage : data.webMessage;
      const trimmed = rawMessage != null ? String(rawMessage).trim() : "";

      if (trimmed) {
        // Fatal: show message and let App stop any further startup/auth APIs.
        dispatch(setGroupConfigFatalMessage(trimmed));
        return { blocked: true };
      }

      // Non-fatal: show `errorDescription` and continue boot.
      toast.error((data.errorDescription && String(data.errorDescription).trim()) || "Unable to load site configuration.");
      dispatch(setGroupConfigFatalMessage(null));
      return { blocked: false };
    }

    // Success path.
    dispatch(setGroupConfigFatalMessage(null));

    const fetchedSiteConfig = {
      ...data,
      colorConfig: (data as any)?.colorConfig,
    } as SiteConfig;

    if (fetchedSiteConfig) {
      dispatch(setSiteConfig(fetchedSiteConfig));
    }

    return { blocked: false };
  } catch (error) {
    console.error("Error fetching group-config:", error);
    toast.error("Unable to load site configuration. Please try again.");
    dispatch(setGroupConfigFatalMessage(null));
    return { blocked: false };
  }
};

// Helper action to update dynamic text colors when theme changes
export const updateDynamicTextColors = () => (dispatch: any, getState: any) => {
  const userDetails = getState().userDetails.userDetails;
  if (userDetails) {
    const dynamicColors = getDynamicTextColors(userDetails);
    dispatch(setDynamicTextColors(dynamicColors));
  }
};